import { useEffect, useRef } from 'react';

import { useNotification } from './provider';

import { apiService } from '@/services/api-service';

// Subscribes to apiService retry events and surfaces a single "Something went
// wrong... retrying..." toast. Multiple concurrent retries collapse into one
// toast (count tracked via ref) so a burst of failing requests doesn't flood
// the user. Mount this once, inside NotificationProvider.
export const RetryToastBridge: React.FC = () => {
	const { showToast, dismiss, showBanner } = useNotification();
	const activeToastIdRef = useRef<string | null>(null);
	const activeCountRef = useRef(0);
	const mountedRef = useRef(true);

	useEffect(() => {
		mountedRef.current = true;

		const handleRetry = () => {
			if (!mountedRef.current) return;
			activeCountRef.current += 1;
			// If a toast is already showing, leave it — its message is still
			// accurate ("Something went wrong... retrying...").
			if (activeToastIdRef.current) return;

			// Long duration; we explicitly dismiss on resolve/fail so it doesn't
			// disappear mid-retry.
			activeToastIdRef.current = showToast('Something went wrong... retrying...', {
				variant: 'warning',
				duration: 30_000,
			});
		};

		const handleResolved = () => {
			if (!mountedRef.current) return;
			activeCountRef.current = Math.max(0, activeCountRef.current - 1);
			if (activeCountRef.current === 0 && activeToastIdRef.current) {
				const id = activeToastIdRef.current;
				activeToastIdRef.current = null;
				dismiss(id);
				showToast('Reconnected', { variant: 'success', duration: 2000 });
			}
		};

		const handleFailed = () => {
			if (!mountedRef.current) return;
			activeCountRef.current = Math.max(0, activeCountRef.current - 1);
			if (activeCountRef.current === 0 && activeToastIdRef.current) {
				const id = activeToastIdRef.current;
				activeToastIdRef.current = null;
				dismiss(id);
				showBanner('We couldn\'t reach the server. Please check your connection.', 'error');
			}
		};

		const unsubRetry = apiService.onRetry(handleRetry);
		const unsubResolved = apiService.onRetryResolved(handleResolved);
		const unsubFailed = apiService.onRetryFailed(handleFailed);

		return () => {
			mountedRef.current = false;
			unsubRetry();
			unsubResolved();
			unsubFailed();
		};
	}, [showToast, dismiss, showBanner]);

	return null;
};
